import { default as throttle } from 'lodash/throttle';

import { AnyObject, Callable } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { CallablePlaceholder } from '../../../types/general';

export interface IInMemoryBufferConfig<T = AnyObject> {
  name: string;
  initialValue: T;
  throttleDelay: number;
  onUpdate: (data: T) => void;
}

/**
 * In memory buffer manager, get and set value with on update callback.
 */
export class InMemoryBuffer<T> {
  public readonly log: Logger = new Logger('🔂BFR');

  private bufferName: string;
  private buffer: T;
  private onUpdate: Callable = CallablePlaceholder;

  public constructor(bufferConfig: IInMemoryBufferConfig<T>) {
    this.bufferName = bufferConfig.name;
    this.buffer = bufferConfig.initialValue;

    if (bufferConfig.onUpdate) {
      this.onUpdate = throttle(
        () => bufferConfig.onUpdate(this.buffer),
        bufferConfig.throttleDelay,
      );
    }

    this.log.info('Init buffer:', this.bufferName, this.buffer);
  }

  /**
   * Get a buffer name for logging or other purposes.
   */
  public get name() {
    return this.bufferName;
  }

  /**
   * Get current value of buffer object.
   */
  public getValue(): T {
    return this.buffer;
  }

  /**
   * Set value to the buffer object and call on update callback.
   * @param value object with updates for buffer
   */
  public setValue(value: T): void {
    this.buffer = value;

    this.onUpdate();
  }

  /**
   * Update value of the buffer object and call on update callback.
   * @param value object with updates for buffer
   */
  public updateValue(value: T): void {
    this.buffer = { ...this.buffer, ...value };

    this.onUpdate();
  }

  /**
   * Clear buffered data and call on update callback.
   */
  public resetValue(): void {
    this.buffer = {} as T;

    this.onUpdate();
  }

  /**
   * Cleanup buffer data and update callback.
   */
  public dispose(): void {
    this.log.info('Dispose buffer:', this.bufferName);

    this.bufferName = '';
    this.buffer = {} as T;
    this.onUpdate = CallablePlaceholder;
  }
}
